import React from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Link,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetOrganizationDocument, type GetOrganizationQuery } from '@/generated/root-types';
import { translateAddressType } from '@/utils/addressLabels';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

export default function OrganizationDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenantId = (location.state as { fromTenantId?: string })?.fromTenantId;

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading, error } = useQuery<GetOrganizationQuery>(GetOrganizationDocument, {
    variables: { id },
    skip: !id,
  });

  const org = data?.organization;

  const handleBack = () => {
    if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else navigate('/customers');
  };

  const listParams = new URLSearchParams();
  if (org?.tenantId) listParams.set('tenantId', org.tenantId);
  if (org?.id) listParams.set('organizationId', org.id);
  const filterQs = listParams.toString() ? `?${listParams.toString()}` : '';

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !org) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">{error?.message ?? t('errors.organizationNotFound')}</Typography>
      </Box>
    );
  }

  const addresses = org.addresses ?? [];
  const contacts = org.contacts ?? [];
  const units = org.units ?? [];

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {org.name}
        </Typography>
        {fromTenantId && (
          <Button size="small" onClick={() => navigate(`/customers/${fromTenantId}`)}>
            {t('common.toCustomer')}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('organizationDetail.masterData')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.name')}
            </Typography>
            <Typography variant="body2">{org.name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('organizationDetail.customer')}
            </Typography>
            <Typography variant="body2">{org.tenant?.name ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.status')}
            </Typography>
            <Typography variant="body2">{org.status ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.type')}
            </Typography>
            <Typography variant="body2">{org.type ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.default')}
            </Typography>
            <Typography variant="body2">
              {org.isDefault ? t('common.yes') : t('common.no')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.created')}
            </Typography>
            <Typography variant="body2">{formatDate(org.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.updated')}
            </Typography>
            <Typography variant="body2">{formatDate(org.updatedAt)}</Typography>
          </Box>
        </Box>
        {org.description && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {org.description}
          </Typography>
        )}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {t('organizationDetail.technicalInfo')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
            >
              {t('common.organizationId')}: {org.id}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
            >
              {t('common.tenantId')}: {org.tenantId}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('orgUnits.title')}
          </Typography>
          <Button component={RouterLink} to={`/organization-units${filterQs}`} size="small">
            {t('organizationDetail.manageOrgUnits')}
          </Button>
        </Box>
        {units.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('common.noData')}
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {units.map((u) => (
              <li key={u.id}>
                <Link component={RouterLink} to={`/organization-units/${u.id}`}>
                  {u.name}
                </Link>
                {u.description ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {u.description}
                  </Typography>
                ) : null}
              </li>
            ))}
          </Box>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('orgAddresses.title')}
          </Typography>
          <Button component={RouterLink} to={`/organization-addresses${filterQs}`} size="small">
            {t('organizationDetail.manageAddresses')}
          </Button>
        </Box>
        {addresses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('common.noData')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {addresses.map((a) => (
              <Card
                key={a.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  transition: (theme) =>
                    theme.transitions.create(['background-color', 'box-shadow']),
                  '&:hover': { bgcolor: 'action.hover', boxShadow: 1 },
                }}
                onClick={() =>
                  navigate(`/organization-addresses/${a.id}`, {
                    state: {
                      addressesListSearch: filterQs,
                      tenantIdOverride: org.tenantId,
                    },
                  })
                }
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2">{translateAddressType(t, a.type)}</Typography>
                    {a.isDefault ? (
                      <Chip
                        label={t('orgAddresses.standardAddress')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : null}
                  </Box>
                  <Typography variant="body2">
                    {[a.addressLine1, a.addressLine2].filter(Boolean).join(', ')}
                    {a.zipCode || a.city
                      ? ` · ${[a.zipCode, a.city].filter(Boolean).join(' ')}`
                      : ''}
                    {a.country ? ` · ${a.country}` : ''}
                  </Typography>
                  {(a.phoneNumber || a.email) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {[a.phoneNumber, a.email].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('orgContacts.title')}
          </Typography>
          <Button component={RouterLink} to={`/organization-contacts${filterQs}`} size="small">
            {t('organizationDetail.manageContacts')}
          </Button>
        </Box>
        {contacts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('common.noData')}
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {contacts.map((c) => (
              <li key={c.id}>
                <Link component={RouterLink} to={`/organization-contacts/${c.id}`}>
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || c.id}
                </Link>
                {c.email && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {c.email}
                  </Typography>
                )}
              </li>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
